import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Card,
    CardContent,
    Grid,
    Typography,
    Tabs,
    Tab,
    Chip,
    Alert,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
    Divider,
    CircularProgress,
    List,
    ListItem,
    ListItemText
} from '@mui/material';
import {
    Visibility as VisibilityIcon,
    ExpandMore as ExpandMoreIcon,
    QuizOutlined as QuizIcon,
    AccessTime as AccessTimeIcon,
    Score as ScoreIcon
} from '@mui/icons-material';

const QuizAnalytics = ({ onError, onLoading }) => {
    const [analytics, setAnalytics] = useState({});
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    
    // State for detailed view
    const [detailedViewOpen, setDetailedViewOpen] = useState(null);
    const [expandedAccordions, setExpandedAccordions] = useState({});
    const [sessionEvents, setSessionEvents] = useState({});


    const fetchAnalytics = useCallback(async () => {
        try {
            setError(null);
            // if (onLoading) onLoading(true); 

            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user-tracking/comprehensive-analytics`);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (data.success) {
                setAnalytics(data.analytics);
            } else {
                const errorMsg = data.error || 'Failed to fetch analytics';
                setError(errorMsg);
                if (onError) onError(errorMsg);
            }
        } catch (err) {
            console.error('Error fetching analytics:', err);
            const errorMsg = 'Failed to fetch analytics';
            setError(errorMsg);
            if (onError) onError(errorMsg);
        } finally {
            if (onLoading) onLoading(false);
        }
    }, [onError, onLoading]); // Dependencies for useCallback

    const getPlayerDisplayName = (session, playerType) => {
        switch (playerType) {
            case 'anonymous':
                return `Anon${session.anonymous_id?.slice(-3) || 'Unknown'}`;
            case 'guest':
                return session.guest_name || session.fname || 'Guest User';
            case 'registered':
                return `${session.fname} ${session.lname || ''}`.trim() || session.email || 'Registered User';
            default:
                return 'Unknown User';
        }
    };


    useEffect(() => {
        fetchAnalytics();
    }, [fetchAnalytics]); // Include fetchAnalytics as dependency

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed': return 'success';
            case 'active': return 'warning';
            case 'abandoned': return 'error';
            default: return 'default';
        }
    };

    // Helper functions for detailed view
    const toggleDetailedView = (playerId) => {
        setDetailedViewOpen(detailedViewOpen === playerId ? null : playerId);
    };

    const handleAccordionChange = (playerId, sessionIndex, sessionId) => (event, isExpanded) => {
        setExpandedAccordions(prev => ({
            ...prev,
            [playerId]: isExpanded ? sessionIndex : null
        }));
        
        // Fetch events when expanding
        if (isExpanded && sessionId && !sessionEvents[sessionId]) {
            fetchQuizEvents(sessionId);
        }
    };

    const fetchQuizEvents = useCallback(async (sessionId) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user-tracking/session/${sessionId}/events`);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            
            setSessionEvents(prev => ({
                ...prev,
                [sessionId]: data.success ? data.events : []
            }));
        } catch (error) {
            console.error('Error fetching quiz events:', error);
            setSessionEvents(prev => ({
                ...prev,
                [sessionId]: []
            }));
        }
    }, []);

    const isLoadingEvents = (sessionId) => {
        return !(sessionId in sessionEvents);
    };

    // Timeline component removed - using simplified sequential display

    const groupSessionsByPlayer = (data, playerType) => {
        const grouped = {};
        data.forEach(session => {
            // For registered users, group by user_id. For others, use session_id/device_id
            let playerId;
            if (playerType === 'registered' && session.user_id) {
                playerId = `${playerType}_${session.user_id}`;
            } else {
                playerId = `${playerType}_${session.user_plays_master_id || session.device_id}`;
            }
            
            if (!grouped[playerId]) {
                grouped[playerId] = {
                    player: getPlayerDisplayName(session, playerType),
                    playerInfo: {
                        email: session.email,
                        guestName: session.guest_name,
                        deviceId: session.device_id,
                        userType: session.user_type || playerType
                    },
                    sessions: []
                };
            }
            grouped[playerId].sessions.push({
                ...session,
                formattedTimestamp: new Date(session.started_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })
            });
        });
        
        // Sort sessions by timestamp (most recent first)
        Object.values(grouped).forEach(playerData => {
            playerData.sessions.sort((a, b) => new Date(b.started_at) - new Date(a.started_at));
        });
        
        return grouped;
    };

    const renderQuizSessionDetails = (session) => {
        return (
            <Box>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={6}>
                        <Card variant="outlined">
                            <CardContent>
                                <Box display="flex" alignItems="center" mb={1}>
                                    <QuizIcon color="primary" sx={{ mr: 1 }} />
                                    <Typography variant="h6">Quiz Information</Typography>
                                </Box>
                                <List dense>
                                    <ListItem>
                                        <ListItemText 
                                            primary="Status" 
                                            secondary={
                                                <Chip
                                                    label={session.status}
                                                    color={getStatusColor(session.status)}
                                                    size="small"
                                                />
                                            }
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemText 
                                            primary="Duration" 
                                            secondary={`${session.total_duration_minutes || 0} minutes`} 
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemText 
                                            primary="Quizlets Completed" 
                                            secondary={session.quizlets_completed || 0} 
                                        />
                                    </ListItem>
                                </List>
                            </CardContent>
                        </Card>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                        <Card variant="outlined">
                            <CardContent>
                                <Box display="flex" alignItems="center" mb={1}>
                                    <ScoreIcon color="success" sx={{ mr: 1 }} />
                                    <Typography variant="h6">Performance</Typography>
                                </Box>
                                <List dense>
                                    <ListItem>
                                        <ListItemText 
                                            primary="Total Score" 
                                            secondary={session.total_score || 0} 
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemText 
                                            primary="Accuracy" 
                                            secondary={`${session.avg_accuracy || 0}%`} 
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemText 
                                            primary="Correct Answers" 
                                            secondary={session.total_correct || 0} 
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemText 
                                            primary="Total Questions" 
                                            secondary={session.total_questions || 0} 
                                        />
                                    </ListItem>
                                </List>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                <Card variant="outlined">
                    <CardContent>
                        <Box display="flex" alignItems="center" mb={1}>
                            <AccessTimeIcon color="info" sx={{ mr: 1 }} />
                            <Typography variant="h6">Quiz Timeline</Typography>
                        </Box>
                        
                        {/* Simple and Clean Timeline */}
                        <Box sx={{ p: 2 }}>
                            
                            {/* Loading State */}
                            {isLoadingEvents(session.user_plays_master_id) ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 3 }}>
                                    <CircularProgress size={20} sx={{ mr: 2 }} />
                                    <Typography variant="body2" color="textSecondary">
                                        Loading timeline...
                                    </Typography>
                                </Box>
                            ) : (
                                
                                /* Timeline Events */
                                <Box>
                                    
                                    {/* 1. Mode Selection */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', py: 2, borderBottom: '1px solid #e0e0e0' }}>
                                        <Box sx={{ 
                                            bgcolor: '#ff9800', 
                                            color: 'white',
                                            borderRadius: '50%',
                                            width: 32,
                                            height: 32,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mr: 2,
                                            fontSize: '16px'
                                        }}>
                                            1
                                        </Box>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#ff9800', mb: 0.5 }}>
                                                🎵 Mode Selection
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {(() => {
                                                    const modeEvent = sessionEvents[session.user_plays_master_id]?.find(e => e.event_name === 'quiz_mode_selected');
                                                    return modeEvent 
                                                        ? `${new Date(modeEvent.event_time).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })} - Normal Mode`  
                                                        : 'Not recorded';
                                                })()}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* 2. Configuration */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', py: 2, borderBottom: '1px solid #e0e0e0' }}>
                                        <Box sx={{ 
                                            bgcolor: '#2196f3', 
                                            color: 'white',
                                            borderRadius: '50%',
                                            width: 32,
                                            height: 32,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mr: 2,
                                            fontSize: '16px'
                                        }}>
                                            2
                                        </Box>
                                            <Box sx={{ flex: 1 }}>
                                            <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#2196f3', mb: 0.5 }}>
                                                ⚙️ Configuration
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {(() => {
                                                    const configEvent = sessionEvents[session.user_plays_master_id]?.find(e => e.event_name === 'quiz_config_next');
                                                    return configEvent 
                                                        ? `${new Date(configEvent.event_time).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })} - Completed` 
                                                        : 'Not recorded';
                                                })()}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* 3. Rules */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', py: 2, borderBottom: '1px solid #e0e0e0' }}>
                                        <Box sx={{ 
                                            bgcolor: '#9c27b0', 
                                            color: 'white',
                                            borderRadius: '50%',
                                            width: 32,
                                            height: 32,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mr: 2,
                                            fontSize: '16px'
                                        }}>
                                            3
                                        </Box>
                                            <Box sx={{ flex: 1 }}>
                                            <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#9c27b0', mb: 0.5 }}>
                                                📋 Rules Reading
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {(() => {
                                                    const rulesEvent = sessionEvents[session.user_plays_master_id]?.find(e => e.event_name === 'quiz_rules_next');
                                                    return rulesEvent 
                                                        ? `${new Date(rulesEvent.event_time).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })} - Completed` 
                                                        : 'Not recorded';
                                                })()}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* 4. Quiz Started */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', py: 2, borderBottom: '1px solid #e0e0e0' }}>
                                        <Box sx={{ 
                                            bgcolor: '#f44336', 
                                            color: 'white',
                                            borderRadius: '50%',
                                            width: 32,
                                            height: 32,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mr: 2,
                                            fontSize: '16px'
                                        }}>
                                            4
                                        </Box>
                                            <Box sx={{ flex: 1 }}>
                                            <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#f44336', mb: 0.5 }}>
                                                🎯 Quiz Started
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {(() => {
                                                    const startEvent = sessionEvents[session.user_plays_master_id]?.find(e => e.event_name === 'quiz_started');
                                                    if (startEvent) {
                                                        const additionalData = JSON.parse(startEvent.additional_data || '{}');
                                                        return `${new Date(startEvent.event_time).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })} - ${additionalData.song_name || 'Quiz'}`;
                                                    }
                                                    return 'Not recorded';
                                                })()}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* 5. Quiz Complete */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', py: 2 }}>
                                        <Box sx={{ 
                                            bgcolor: '#4caf50', 
                                            color: 'white',
                                            borderRadius: '50%',
                                            width: 32,
                                            height: 32,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mr: 2,
                                            fontSize: '16px'
                                        }}>
                                            5
                                        </Box>
                                            <Box sx={{ flex: 1 }}>
                                            <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#4caf50', mb: 0.5 }}>
                                                🎉 Quiz Complete
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {(() => {
                                                    const completeEvent = sessionEvents[session.user_plays_master_id]?.find(e => e.event_name === 'quiz_complete');
                                                    return completeEvent 
                                                        ? `${new Date(completeEvent.event_time).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })} - Finished` 
                                                        : 'Not recorded';
                                                })()}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        );
    };

    const renderDetailedView = (playerType, data) => {
        const groupedData = groupSessionsByPlayer(data, playerType);
        
        return (
            <Box sx={{ mt: 2 }}>
                {Object.entries(groupedData).map(([playerId, playerData]) => (
                    <Card key={playerId} sx={{ mb: 3, border: detailedViewOpen === playerId ? '2px solid' : '1px solid' }}>
                        <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Box>
                                    <Typography variant="h6">{playerData.player}</Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        {playerData.playerInfo.userType === 'anonymous' 
                                            ? `Device: ${playerData.playerInfo.deviceId?.slice(-8)}`
                                            : playerData.playerInfo.email || playerData.playerInfo.guestName
                                        }
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        {playerData.sessions.length} quiz session{playerData.sessions.length !== 1 ? 's' : ''}
                                    </Typography>
                                </Box>
                                <IconButton 
                                    onClick={() => toggleDetailedView(playerId)}
                                    color="primary"
                                    sx={{ backgroundColor: detailedViewOpen === playerId ? 'action.selected' : 'transparent' }}
                                >
                                    <VisibilityIcon />
                                </IconButton>
                            </Box>
                            
                            {detailedViewOpen === playerId && (
                                <Box sx={{ mt: 2 }}>
                                    <Divider sx={{ mb: 2 }} />
                                    {playerData.sessions.map((session, index) => (
                                        <Accordion 
                                            key={session.user_plays_master_id} 
                                            expanded={expandedAccordions[playerId] === index}
                                            onChange={handleAccordionChange(playerId, index, session.user_plays_master_id)}
                                            sx={{ mb: 1 }}
                                        >
                                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                <Box display="flex" alignItems="center" width="100%">
                                                    <QuizIcon sx={{ mr: 1, color: 'primary.main' }} />
                                                    <Box flexGrow={1}>
                                                        <Typography variant="subtitle1">
                                                            Quiz #{index + 1}
                                                        </Typography>
                                                        <Typography variant="body2" color="textSecondary">
                                                            {session.formattedTimestamp}
                                                        </Typography>
                                                    </Box>
                                                    <Chip
                                                        label={session.status}
                                                        color={getStatusColor(session.status)}
                                                        size="small"
                                                    />
                                                </Box>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                {renderQuizSessionDetails(session)}
                                            </AccordionDetails>
                                        </Accordion>
                                    ))}
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                ))}
            </Box>
        );
    };

    const renderPlayerTypeAnalytics = (playerType, data) => {
        if (!data || data.length === 0) {
            return (
                <Alert severity="info">
                    No data available for {playerType} players
                </Alert>
            );
        }

        return renderDetailedView(playerType, data);
    };

    const getTotalStats = () => {
        const registered = analytics.registered || [];
        const guest = analytics.guest || [];
        const anonymous = analytics.anonymous || [];

        const totalPlayers = registered.length + guest.length + anonymous.length;
        const totalScore = [...registered, ...guest, ...anonymous]
            .reduce((sum, session) => sum + (session.total_score || 0), 0);
        const totalQuizlets = [...registered, ...guest, ...anonymous]
            .reduce((sum, session) => sum + (session.quizlets_completed || 0), 0);

        return { totalPlayers, totalScore, totalQuizlets };
    };

    const stats = getTotalStats();

    return (
        <Box>
            <Typography variant="h5" gutterBottom>
                Quiz Analytics
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Total Players
                            </Typography>
                            <Typography variant="h4">
                                {stats.totalPlayers}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Total Score
                            </Typography>
                            <Typography variant="h4">
                                {stats.totalScore.toLocaleString()}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Total Quizlets
                            </Typography>
                            <Typography variant="h4">
                                {stats.totalQuizlets}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Player Types
                            </Typography>
                            <Typography variant="h6">
                                {analytics.registered?.length || 0} Registered
                            </Typography>
                            <Typography variant="h6">
                                {analytics.guest?.length || 0} Guest
                            </Typography>
                            <Typography variant="h6">
                                {analytics.anonymous?.length || 0} Anonymous
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Tabs for different player types */}
            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
                {/* <Tab label={`All Players (${stats.totalPlayers})`} /> */}
                <Tab label={`Registered (${analytics.registered?.length || 0})`} />
                <Tab label={`Guest (${analytics.guest?.length || 0})`} />
                <Tab label={`Anonymous (${analytics.anonymous?.length || 0})`} />
            </Tabs>

            <Box sx={{ mt: 3 }}>
                {activeTab === 0 && (
                    <Box>
                        <Typography variant="h6" gutterBottom>Registered Users</Typography>
                        {renderPlayerTypeAnalytics('registered', analytics.registered)}
                    </Box>
                )}

                {activeTab === 1 && (
                    <Box>
                        <Typography variant="h6" gutterBottom>Guest Users</Typography>
                        {renderPlayerTypeAnalytics('guest', analytics.guest)}
                    </Box>
                )}

                {activeTab === 2 && (
                    <Box>
                        <Typography variant="h6" gutterBottom>Anonymous Players</Typography>
                        {renderPlayerTypeAnalytics('anonymous', analytics.anonymous)}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default QuizAnalytics;
